<template>
    <div id="app">
        <div :style="securityLocked ? {visibility: 'hidden'} : null" :inert="securityLocked ? '' : null" :aria-hidden="securityLocked ? 'true' : null">
        <TelegramInstallPromotion/>
        <TelegramDatepicker v-if="isWebAppPage"/>
        <main-component v-else-if="isInstallPage || applicationLifecycleStarted"/>
        <div v-else class="application-bootstrap" aria-live="polite">
            <img src="/icons/icon-192.png" alt="" class="application-bootstrap__icon">
            <span>Загружаем приложение…</span>
        </div>
        <NotificationPermissionPrompt/>
        <AppFileViewer/>
        <AppUpdateModal/>
        <RequestEmployeePanel :controller="requestEmployeePreview"/>
        </div>
        <SessionGuard @locked="securityLocked = $event"/>
    </div>
</template>

<script>
import mainComponent from './views/Template/Template.vue';
import SessionGuard from './components/Security/SessionGuard.vue';
import AppUpdateModal from './components/Modals/AppUpdateModal.vue';
import interceptors from './utils/interceptors';
import TelegramDatepicker from "@/views/TelegramDatepicker/TelegramDatepicker";
import { AppFileViewer } from "@/components/AppFileViewer";
import NotificationPermissionPrompt from "@/components/NotificationPermissionPrompt";
import TelegramInstallPromotion from "@/components/TelegramInstallPromotion";
import {initializeAppInstallationTracking} from "@/utils/appInstallation";
import api from '@/utils/api';
import RequestEmployeePanel from '@/components/RequestEmployeePanel.vue';
import {createRequestEmployeePreview} from '@/utils/requestEmployeePreview';

export default {
    name: 'app',
    provide() {
        return {requestEmployeeContext: {
            isEnabled: () => true,
            load: target => this.requestEmployeePreview.load(target),
            open: (target, trigger) => this.requestEmployeePreview.open(target, trigger)
        }};
    },
    data() {
        return {
            securityLocked: false,
            requestEmployeePreview: createRequestEmployeePreview((url, options) => api.get(url, options)),
            stopInstallationTracking: null,
            applicationLifecycleStarted: false,
            applicationLifecyclePromise: null,
            resizeHandler: null
        };
    },
    computed: {
        isWebAppPage() {
            return this.$route.path === `/telegram-datepicker`;
        },
        isInstallPage() {
            return this.$route.name === 'install-app';
        }
    },
    components: {
        SessionGuard,
        AppUpdateModal,
        RequestEmployeePanel,
        mainComponent,
        TelegramDatepicker,
        TelegramInstallPromotion,
        NotificationPermissionPrompt,
        AppFileViewer
    },
    mixins: [
        interceptors
    ],
    watch: {
        '$route.fullPath'() { this.requestEmployeePreview.reset(); },
        '$store.state.token'(next, previous) {
            if (!next || !previous) this.requestEmployeePreview.reset();
        },
        isInstallPage(isInstallPage) {
            if (!isInstallPage) {
                this.startApplicationLifecycle();
            }
        }
    },
    mounted() {
        this.stopInstallationTracking = initializeAppInstallationTracking();
        this.startApplicationLifecycle();
    },
    beforeDestroy() {
        this.requestEmployeePreview.reset();
        if (this.stopInstallationTracking) {
            this.stopInstallationTracking();
            this.stopInstallationTracking = null;
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
    },
    methods: {
        startApplicationLifecycle() {
            if (this.isInstallPage || this.applicationLifecycleStarted) {
                return this.applicationLifecyclePromise || Promise.resolve();
            }
            if (this.applicationLifecyclePromise) {
                return this.applicationLifecyclePromise;
            }

            this.applicationLifecyclePromise = this.refreshCurrentAuthorization()
                .then(() => {
                    if (this.isInstallPage || this.applicationLifecycleStarted) {
                        return;
                    }
                    this.$store.dispatch(`updateAbility`, this);
                    // Не показываем кликабельный интерфейс до обновления пользователя и CASL:
                    // иначе пункты меню с v-if появляются уже после первого отображения.
                    this.applicationLifecycleStarted = true;
                    this.resizeHandler = () => this.$store.dispatch(`onScreenResize`);
                    window.addEventListener('resize', this.resizeHandler);
                    this.$store.dispatch(`lifecycle_init`);
                })
                .finally(() => {
                    this.applicationLifecyclePromise = null;
                });
            return this.applicationLifecyclePromise;
        },
        async refreshCurrentAuthorization() {
            if (this.$route.matched.some(({meta}) => meta.platform === true)) {
                return;
            }
            if (this.$store.getters.isTelegramWebApps) {
                return;
            }
            if (!this.$store.getters.isAuthorized || !this.$store.state.refreshToken) {
                return;
            }
            await this.$store.dispatch(`refreshAuthorization`, {vm: this}).catch(() => {});
        }
    }
}
</script>
<style lang="stylus">
.application-bootstrap
    display flex
    min-height 100vh
    min-height 100dvh
    align-items center
    justify-content center
    flex-direction column
    gap 12px
    color #5f6b7a
    background #f4f7fb
    font-size 14px

.application-bootstrap__icon
    width 54px
    height 54px
    border-radius 15px
    box-shadow 0 8px 24px rgba(34, 75, 126, .14)
</style>
